import React, { createContext, useContext, useEffect, useState } from 'react';
import { getMultimarket } from "src/api/services/multimarket/multimarketApi";
import { MarketApiResponse } from "src/model/shop.model";
import appState from "src/model/state";
import {
  checkURLIfGB,
  getCurrentLanguage,
  getEntryISOPathExtraction,
  getOriginSesion,
} from "../BasePathTools";
import { getUserFromCookies, registerUserCookies, searchToken } from "../TokenTools";
import { searchEnvironment } from "../EnvironmentTools";
import { 
  extractDetailsFromZaraUrl, 
  extractProductIdFromUrl, 
  isAskForDetailsPage, 
  isCartPage, 
  isMySellsPage, 
  isOrdersPage, 
  isProductDetailPage, 
  isProfilePage, 
  isUserFromZaraMenu, 
  redirectToPreownedResell,
  isCommunicationPage 
} from "../redirect/manager-redirect";
import {
  InitialOriginPathExtraction,
  getUserCountryIso,
  goToZara,
  initialLanguagePathExtraction,
  proccesCountry,
  processISOMarket,
  processStoreId,
  sessionOriginProcess,
  resetConfigurationSessioObj as sessionObjConfigReset,
} from "../init/configuration";
import { initialize } from "../init/initialize";
import { OpenapiSingleton } from "../OpenapiSingleton";
import { Loader } from 'src/components/loader/loader';
import { t } from 'i18next';

interface ApplicationInitializerContextType {
  isInitialized: boolean;
  isLoading: boolean;
  error: Error | null;
}

const ApplicationInitializerContext = createContext<ApplicationInitializerContextType>({
  isInitialized: false,
  isLoading: true,
  error: null
});


export const useApplicationInitializer = () => useContext(ApplicationInitializerContext);

interface ApplicationInitializerProviderProps {
  children: React.ReactNode;
}

export const ApplicationInitializerProvider: React.FC<ApplicationInitializerProviderProps> = ({ 
  children
}) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const generateUserCookies = async (id: string) => {
    if(!(getUserFromCookies()?.user_type)) {
      try {
        const guestUser = await OpenapiSingleton.storeFront.generateUserGuest({storeId: Number(id)});
        registerUserCookies(guestUser);
      } catch (e) {
        console.log('Error trying to create guest user', e);
      }
    } 
  };

  const configurationExtraction = async (
    market: MarketApiResponse,
  ): Promise<void> => {
    sessionObjConfigReset();
    sessionOriginProcess();
    InitialOriginPathExtraction();
    initialLanguagePathExtraction(market);
    proccesCountry(market?.countryIso);
    processISOMarket(market?.marketIso);
    processStoreId(market?.id);
  };

  const shopInitialization = async (market: MarketApiResponse): Promise<void> => {
    appState.execute("SHOP_MARKET_SET_CONFIG", market);
    await initialize();
  };

  const findCountryOrigin = async (): Promise<string | void> => {
    try {
      return await getUserCountryIso();
    } catch {
      goToZara();
    }
  };

  const intializeEnv = async (): Promise<void> => {
    try {
      await searchEnvironment();
    } catch (e) {
      throw new Error(`Environment initialization failed: ${e}`);
    }
  };

  const handleRedirectionBasedOnUrl = async (marketData: any, urlParamsFromZara: any): Promise<void> => {
    let section = null;
    let productId = null;
    let path = null;

    if (isProductDetailPage()) {
      section = 'product-detail';
      productId = extractProductIdFromUrl();
    } else if (isOrdersPage()) {
      section = 'orders';
    } else if (isMySellsPage()) {
      section = 'my-sells';
    } else if (isAskForDetailsPage()) {
      section = 'ask-for-details';
      productId = extractProductIdFromUrl();
    } else if (isCommunicationPage()) {
      section = 'communication'; 
      productId = extractProductIdFromUrl();
    } else if (isCartPage()) {
      section = 'cart';
      productId = getOriginSesion();
    } else if (isProfilePage()) {
      section = 'profile';
      productId = getOriginSesion();
    }

    await redirectToPreownedResell(marketData, urlParamsFromZara, section, productId).then((path) => {
      appState.execute("SET_INIT_PATH", path);
    });
  };

  const initializerActions = async (multimarketParcialData: MarketApiResponse) => {
    try {
      await configurationExtraction(multimarketParcialData);
      const multimarketFinalData = await getMultimarket({
        iso: multimarketParcialData?.countryIso?.toLocaleLowerCase(), 
        lang: getCurrentLanguage(), 
        purifiedUrl: true
      });
      await shopInitialization(multimarketFinalData);
      await searchToken();
      await searchEnvironment();
      await generateUserCookies(multimarketParcialData?.id);
    } catch (e) {
      throw new Error(`${e}`);
    }
  };

  const marketOriginActions = async () => {
    const parcialCountry = await findCountryOrigin();
    if (parcialCountry) {
      await applicationInit(parcialCountry);
    } else {
      goToZara();
    }
  };

  const applicationInit = async (countryIsoParam?: string): Promise<string | null> => {
    setIsLoading(true);
    try {
      await checkURLIfGB(); 
      const originData = countryIsoParam || getEntryISOPathExtraction();
      
      if (originData) {
        await intializeEnv(); 
        const marketData = await getMultimarket({iso: originData, purifiedUrl: true});
        
        if (isUserFromZaraMenu()) {
          const urlParamsFromZara = extractDetailsFromZaraUrl();
          await handleRedirectionBasedOnUrl(marketData, urlParamsFromZara);
        }
        
        if (marketData?.isMarketIso) {
          await marketOriginActions();
        } else {
          await initializerActions(marketData);
        }
        
        setIsInitialized(true);
        return null;
      }
      return null;
    } catch (e) {
      const originRegexdata = location?.pathname?.match(/^\/([^/]+)/);
      if (originRegexdata?.length > 0 && originRegexdata[1]?.toLowerCase() === "gb") {
        const updatedUrl = location.href.replace(/\/gb\//i, '/uk/');
        window.location.href = updatedUrl;
      } else {
        goToZara();
        setError(e instanceof Error ? e : new Error(`${e}`));
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const inicalizer = async () => {
        await applicationInit();
        setIsInitialized(true);
      };
      inicalizer();
  }, []);


  return (
    <ApplicationInitializerContext.Provider value={{ isInitialized, isLoading, error }}>
      <main className="resell-theme" id="main-content">
        <Loader fullscreen visible={isLoading && !isInitialized} />
        {!isLoading && isInitialized && children}
      </main>
    </ApplicationInitializerContext.Provider>
  );
};
